import React, { useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

const WelcomeTour = ({ open, onClose, onComplete }) => {
    const driverObj = useRef(null);

    useEffect(() => {
        if (!open) return;

        const handleComplete = async () => {
            if (driverObj.current) {
                driverObj.current.destroy();
            }
            if (onComplete) {
                await onComplete();
            }
            if (onClose) {
                onClose();
            }
        };

        // Check if elements exist before starting tour
        const checkElements = () => {
            const expandButton = document.querySelector('[data-tour="expand-post"]');
            return !!expandButton;
        };

        // Initialize driver.js
        driverObj.current = driver({
            showProgress: true,
            showButtons: ['next', 'previous', 'close'],
            allowClose: true,
            overlayColor: 'rgba(0, 0, 0, 0.75)',
            smoothScroll: true,
            steps: [
                {
                    element: '[data-tour="expand-post"]',
                    popover: {
                        title: 'Step 1: Expand the Posts',
                        description: 'Click on any post card to expand it and view the full content. You can see the title, category, and other details.',
                        side: 'bottom',
                        align: 'center',
                    },
                },
                {
                    element: '[data-tour="read-post"]',
                    popover: {
                        title: 'Step 2: Read the Post',
                        description: 'Once expanded, you can read the complete post content, view attachments, and see all the details about the FAQ.',
                        side: 'top',
                        align: 'center',
                    },
                },
                {
                    element: '[data-tour="read-ok-button"]',
                    popover: {
                        title: 'Step 3: Click "Read OK"',
                        description: 'If you have read and understood the post, click the "READ OK" button to mark it as accepted. This confirms you have reviewed the information.',
                        side: 'top',
                        align: 'center',
                    },
                },
            ],
            onHighlighted: (element) => {
                // Auto-expand the card when highlighting the expand button
                if (element && element.getAttribute('data-tour') === 'expand-post') {
                    // Check if card is not already expanded
                    const card = element.closest('.MuiCard-root');
                    if (card) {
                        const collapse = card.querySelector('.MuiCollapse-root');
                        if (collapse && !collapse.classList.contains('MuiCollapse-entered')) {
                            // Small delay to let the highlight animation complete
                            setTimeout(() => {
                                element.click();
                            }, 300);
                        }
                    }
                }
            },
            onDestroyStarted: () => {
                handleComplete();
            },
            onDestroyed: () => {
                handleComplete();
            },
        });

        // Start the tour after ensuring elements are rendered
        const startTour = () => {
            if (checkElements()) {
                setTimeout(() => {
                    if (driverObj.current && open) {
                        driverObj.current.drive();
                    }
                }, 500);
            } else {
                // Retry after a short delay
                setTimeout(startTour, 300);
            }
        };

        startTour();

        // Cleanup function
        return () => {
            if (driverObj.current) {
                driverObj.current.destroy();
            }
        };
    }, [open, onClose, onComplete]);

    // This component doesn't render anything visible
    // Driver.js handles all the UI
    return null;
};

export default WelcomeTour;
